import React, { useState, useEffect } from "react";
import { Routes, Route, NavLink, Navigate } from "react-router-dom";
import Layout from "../components/Layout";
import { userService } from "../services/userService";
import { sidebarMenuService } from "../services/sidebarMenuService";
import { useAuth } from "../context/AuthContext";
import AdminPermissions from "./AdminPermissions";
import CuttingManagement from "./CuttingManagement";
import TrimmingManagement from "./TrimmingManagement";
import CuttingApplications from "./CuttingApplications";
import TrimmingApplications from "./TrimmingApplications";
import GardenInspectorDashboard from "./GardenInspectorDashboard";
import ViewCuttingApplication from "./ViewCuttingApplication";
import GardenManagement from "./GardenManagement";

const AdminDashboard = () => {
  const { user, hasPermission } = useAuth();
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState({});

  // Check if user has access to a menu item based on role
  const canAccessMenuItem = (menuItem) => {
    if (!user) return false;
    
    const role = user.role?.toLowerCase() || "";
    const roleId = user.role_id;
    
    // Superadmin/Commissioner has access to everything
    if (role === "superadmin" || role === "commissioner" || roleId === 1 || roleId === 6) {
      return true;
    }
    
    // Check specific menu items based on role
    const route = menuItem.route?.toLowerCase() || "";
    
    // User Management - only for Superadmin/Commissioner
    if (route === "" || route.includes("user")) {
      return role === "superadmin" || role === "commissioner" || roleId === 1 || roleId === 6;
    }
    
    // Permissions - only for Superadmin/Commissioner
    if (route.includes("permission")) {
      return role === "superadmin" || role === "commissioner" || roleId === 1 || roleId === 6;
    }
    
    // Cutting/Trimming Applications - all admin roles can access
    if (route.includes("cutting") || route.includes("trimming")) {
      return true; // All admin roles can view applications
    }
    
    return true; // Default: allow access
  };

  const loadMenuItems = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      let menuRes = null;
      
      // Priority 1: Get menu items assigned to role (role-based from backend)
      if (user.role_id) {
        try {
          menuRes = await sidebarMenuService.getAssignedMenuItems(user.role_id, null);
          // If role-based menu items found, use them
          if (menuRes && menuRes.data && menuRes.data.data && menuRes.data.data.length > 0) {
            const items = menuRes.data.data;
            const parents = items.filter(item => !item.parent_id);
            const children = items.filter(item => item.parent_id);
            
            // Attach children to parents
            const organized = parents.map(parent => ({
              ...parent,
              children: children.filter(child => child.parent_id === parent.id)
            }));
            
            setMenuItems(organized);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error("Error loading role-based menu items:", error);
        }
      }
      
      // Priority 2: If role-based fails, try admin-specific assignments
      if (user.id && !menuRes) {
        try {
          menuRes = await sidebarMenuService.getAssignedMenuItems(null, user.id);
          if (menuRes && menuRes.data && menuRes.data.data && menuRes.data.data.length > 0) {
            const items = menuRes.data.data;
            const parents = items.filter(item => !item.parent_id);
            const children = items.filter(item => item.parent_id);
            
            const organized = parents.map(parent => ({
              ...parent,
              children: children.filter(child => child.parent_id === parent.id)
            }));
            
            setMenuItems(organized);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error("Error loading admin-specific menu items:", error);
        }
      }
      
      // Priority 3: Fallback to default menu items if backend returns empty or fails
      // Backend should handle role-based filtering, so we show fallback only if no data
      if (!menuRes || !menuRes.data || !menuRes.data.data || menuRes.data.data.length === 0) {
        // Use role-based fallback menu items
        const fallbackMenus = [];
        const role = user.role?.toLowerCase() || "";
        const roleId = user.role_id;
        
        // User Management - Only for Superadmin/Commissioner
        if (role === "superadmin" || role === "commissioner" || roleId === 1 || roleId === 6) {
          fallbackMenus.push({ id: 1, title: "User Management", route: "", icon: null, children: [] });
        }
        
        // Cutting Applications - All admin roles
        fallbackMenus.push({ id: 2, title: "Cutting Applications", route: "cutting", icon: null, children: [] });
        
        // Trimming Applications - All admin roles
        fallbackMenus.push({ id: 3, title: "Trimming Applications", route: "trimming", icon: null, children: [] });
        
        // Set Permissions - Only for Superadmin/Commissioner
        if (role === "superadmin" || role === "commissioner" || roleId === 1 || roleId === 6) {
          fallbackMenus.push({ id: 4, title: "Set Permissions", route: "permissions", icon: null, children: [] });
        }
        
        setMenuItems(fallbackMenus);
      }
    } catch (error) {
      console.error("Error loading menu items:", error);
      // Final fallback if everything fails
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMenuItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  // Get role display name
  const getRoleDisplayName = () => {
    if (!user) return "Admin";
    const role = user.role || "";
    return role;
  };

  // Get role-based color scheme
  const getRoleColors = () => {
    if (!user) return { 
      from: "from-blue-500", 
      to: "to-blue-600", 
      text: "text-blue-100",
      activeBg: "bg-blue-600"
    };
    const role = user.role?.toLowerCase() || "";
    
    switch (role) {
      case "garden inspector":
        return { 
          from: "from-blue-500", 
          to: "to-blue-600", 
          text: "text-blue-100",
          activeBg: "bg-blue-600"
        };
      case "divisional officer":
        return { 
          from: "from-purple-500", 
          to: "to-purple-600", 
          text: "text-purple-100",
          activeBg: "bg-purple-600"
        };
      case "garden superintendent":
      case "superintendent":
        return { 
          from: "from-orange-500", 
          to: "to-orange-600", 
          text: "text-orange-100",
          activeBg: "bg-orange-600"
        };
      case "additional":
      case "additional commissioner":
        return { 
          from: "from-indigo-500", 
          to: "to-indigo-600", 
          text: "text-indigo-100",
          activeBg: "bg-indigo-600"
        };
      case "commissioner":
      case "superadmin":
        return { 
          from: "from-red-500", 
          to: "to-red-600", 
          text: "text-red-100",
          activeBg: "bg-red-600"
        };
      default:
        return { 
          from: "from-blue-500", 
          to: "to-blue-600", 
          text: "text-blue-100",
          activeBg: "bg-blue-600"
        };
    }
  };

  const roleColors = getRoleColors();

  // Helper function to check if user can access a route
  const canAccessRoute = (routePath) => {
    if (!user) return false;
    const role = user.role?.toLowerCase() || "";
    const roleId = user.role_id;
    
    // Superadmin/Commissioner has access to everything
    if (role === "superadmin" || role === "commissioner" || roleId === 1 || roleId === 6) {
      return true;
    }
    
    // User Management and Permissions - only for Superadmin/Commissioner
    if (routePath === "" || routePath === "permissions") {
      return false;
    }
    
    // Cutting/Trimming - all admin roles can access
    return true;
  };

  return (
    <Layout title={`${getRoleDisplayName()} Dashboard`}>
      <div className="flex">
        <aside className="w-64 bg-white shadow-md p-4">
          {/* Admin Profile Section with Role */}
          {user && (
            <div className={`mb-6 p-4 bg-gradient-to-r ${roleColors.from} ${roleColors.to} rounded-lg text-white`}>
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-xl font-bold">
                  {user.first_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || "A"}
                </div>
                <div>
                  <h3 className="font-semibold text-sm">
                    {user.first_name} {user.last_name}
                  </h3>
                  <p className={`text-xs ${roleColors.text} font-bold`}>{getRoleDisplayName()}</p>
                </div>
              </div>
              {user.division && (
                <div className={`text-xs ${roleColors.text}`}>
                  <span className="font-medium">Division:</span> {user.division}
                </div>
              )}
              {user.email && (
                <div className={`text-xs ${roleColors.text} mt-1`}>
                  <span className="font-medium">Email:</span> {user.email}
                </div>
              )}
            </div>
          )}

          <nav className="space-y-1">
            {loading ? (
              <div className="text-center py-4 text-gray-500">Loading menu...</div>
            ) : (
              <>
                {menuItems.length > 0 && (
                  menuItems
                    // Backend already filters menu items by role, so no need to filter again
                    .map((item) => {
                      const hasChildren = item.children && item.children.length > 0;
                      const isExpanded = expandedMenus[item.id];
                      
                      return (
                        <div key={item.id}>
                          {hasChildren ? (
                            <>
                              <button
                                onClick={() => toggleMenu(item.id)}
                                className={`w-full flex items-center justify-between px-4 py-2 rounded-md ${
                                  "text-gray-700 hover:bg-gray-100"
                                }`}
                              >
                                <span>{item.title}</span>
                                <span className="text-xs">{isExpanded ? "▼" : "▶"}</span>
                              </button>
                              {isExpanded && (
                                <div className="ml-4 mt-1 space-y-1">
                                  {item.children
                                    // Backend already filters menu items by role
                                    .map((child) => (
                                      <NavLink
                                        key={child.id}
                                        to={child.route || ""}
                                        end={!child.route}
                                        className={({ isActive }) =>
                                          `block px-4 py-2 rounded-md text-sm ${
                                            isActive ? `${roleColors.activeBg} text-white` : "text-gray-600 hover:bg-gray-100"
                                          }`
                                        }
                                      >
                                        {child.title}
                                      </NavLink>
                                    ))}
                                </div>
                              )}
                            </>
                          ) : (
                            <NavLink
                              to={item.route || ""}
                              end={!item.route}
                              className={({ isActive }) =>
                                `block px-4 py-2 rounded-md ${
                                  isActive ? `${roleColors.activeBg} text-white` : "text-gray-700 hover:bg-gray-100"
                                }`
                              }
                            >
                              {item.title}
                            </NavLink>
                          )}
                        </div>
                      );
                    })
                )}
                
                {/* Always show these menu items - both when backend menu exists and when it doesn't */}
                <div className="mt-2 pt-2 border-t border-gray-200">
                  {/* Cutting Applications - All admin roles */}
                  <NavLink
                    to="cutting"
                    className={({ isActive }) =>
                      `block px-4 py-2 rounded-md ${
                        isActive ? `${roleColors.activeBg} text-white` : "text-gray-700 hover:bg-gray-100"
                      }`
                    }
                  >
                    Cutting Applications
                  </NavLink>
                  
                  {/* Trimming Applications - All admin roles */}
                  <NavLink
                    to="trimming"
                    className={({ isActive }) =>
                      `block px-4 py-2 rounded-md ${
                        isActive ? `${roleColors.activeBg} text-white` : "text-gray-700 hover:bg-gray-100"
                      }`
                    }
                  >
                    Trimming Applications
                  </NavLink>
                  
                  {/* Garden Management - All admin roles */}
                  <NavLink
                    to="gardens"
                    className={({ isActive }) =>
                      `block px-4 py-2 rounded-md ${
                        isActive ? `${roleColors.activeBg} text-white` : "text-gray-700 hover:bg-gray-100"
                      }`
                    }
                  >
                    Garden Management
                  </NavLink>
                </div>
                
                {/* Additional menu items for Superadmin/Commissioner if no backend menu */}
                {menuItems.length === 0 && (
                  <>
                    {/* User Management - Only for Superadmin/Commissioner */}
                    {(user?.role?.toLowerCase() === "superadmin" || 
                      user?.role?.toLowerCase() === "commissioner" || 
                      user?.role_id === 1 || 
                      user?.role_id === 6) && (
                      <NavLink
                        to=""
                        end
                        className={({ isActive }) =>
                          `block px-4 py-2 rounded-md ${
                            isActive ? `${roleColors.activeBg} text-white` : "text-gray-700 hover:bg-gray-100"
                          }`
                        }
                      >
                        User Management
                      </NavLink>
                    )}
                    
                    {/* Set Permissions - Only for Superadmin/Commissioner */}
                    {(user?.role?.toLowerCase() === "superadmin" || 
                      user?.role?.toLowerCase() === "commissioner" || 
                      user?.role_id === 1 || 
                      user?.role_id === 6) && (
                      <NavLink
                        to="permissions"
                        className={({ isActive }) =>
                          `block px-4 py-2 rounded-md ${
                            isActive ? `${roleColors.activeBg} text-white` : "text-gray-700 hover:bg-gray-100"
                          }`
                        }
                      >
                        Set Permissions
                      </NavLink>
                    )}
                  </>
                )}
              </>
            )}
          </nav>
        </aside>

        <div className="flex-1 ml-6">
          <Routes>
            {/* User Management - Only for Superadmin/Commissioner */}
            {canAccessRoute("") ? (
              <Route path="" element={<AdminDashboardContent />} />
            ) : (
              <Route path="" element={<Navigate to="cutting" replace />} />
            )}
            
            {/* Cutting Applications - All admin roles */}
            {/* More specific routes first */}
            <Route path="cutting/view/:id" element={<ViewCuttingApplication />} />
            <Route path="cutting/total" element={<CuttingApplications />} />
            <Route path="cutting/approved" element={<CuttingApplications />} />
            <Route path="cutting/pending" element={<CuttingApplications />} />
            <Route path="cutting/rejected" element={<CuttingApplications />} />
            <Route path="cutting" element={<CuttingManagement />} />
            
            {/* Trimming Applications - All admin roles */}
            <Route path="trimming" element={<TrimmingManagement />} />
            <Route path="trimming/total" element={<TrimmingApplications />} />
            <Route path="trimming/approved" element={<TrimmingApplications />} />
            <Route path="trimming/pending" element={<TrimmingApplications />} />
            <Route path="trimming/rejected" element={<TrimmingApplications />} />
            
            {/* Garden Management - All admin roles */}
            <Route path="gardens" element={<GardenManagement />} />
            
            {/* Permissions - Only for Superadmin/Commissioner */}
            {canAccessRoute("permissions") && (
              <Route path="permissions" element={<AdminPermissions />} />
            )}
          </Routes>
        </div>
      </div>
    </Layout>
  );
};

const AdminDashboardContent = () => {
  const { hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    password: "",
    roleId: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    userId: null,
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        userService.getUsers(),
        userService.getRoles(),
      ]);
      setUsers(usersRes.data.data);
      setRoles(rolesRes.data.data.filter((r) => r.role_name !== "Superadmin"));
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await userService.createUser(formData);
      alert("User created successfully!");
      setFormData({ name: "", mobile: "", password: "", roleId: "" });
      setShowForm(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.message || "Error creating user");
    }
    setLoading(false);
  };

  const handleRoleUpdate = async (userId, roleId) => {
    try {
      await userService.updateUser(userId, { role_id: roleId });
      alert("User role updated successfully!");
      loadData();
    } catch (error) {
      alert("Error updating user role");
    }
  };

  const handleBlockUnblock = async (user) => {
    const action = user.is_blocked ? "unblock" : "block";
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }
    try {
      setLoading(true);
      await userService.updateUser(user.id, { is_blocked: user.is_blocked ? 0 : 1 });
      alert(`User ${action}ed successfully!`);
      loadData();
    } catch (error) {
      alert("Error: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert("Passwords do not match!");
      return;
    }
    if (passwordData.newPassword.length < 8) {
      alert("Password must be at least 8 characters long!");
      return;
    }
    try {
      setLoading(true);
      await userService.updateUser(passwordData.userId, { password: passwordData.newPassword });
      alert("Password updated successfully!");
      setShowPasswordModal(false);
      setPasswordData({
        userId: null,
        newPassword: "",
        confirmPassword: "",
      });
      loadData();
    } catch (error) {
      alert("Error: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const openPasswordModal = (user) => {
    setPasswordData({
      userId: user.id,
      newPassword: "",
      confirmPassword: "",
    });
    setShowPasswordModal(true);
  };

  return (
    <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">User Management</h2>
          {hasPermission("create_user") && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              {showForm ? "Cancel" : "Create User"}
            </button>
          )}
        </div>

        {showForm && hasPermission("create_user") && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h3 className="text-xl font-bold mb-4">Create New User</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile</label>
                <input
                  type="text"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  value={formData.roleId}
                  onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Select Role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.role_name}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? "Creating..." : "Create User"}
              </button>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">{user.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{user.mobile}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{user.role_name || "No Role"}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        user.is_blocked
                          ? "bg-red-100 text-red-800"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {user.is_blocked ? "Blocked" : "Active"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {hasPermission("assign_role") && (
                        <select
                          value={user.role_id || ""}
                          onChange={(e) => handleRoleUpdate(user.id, e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                          title="Change Role"
                        >
                          <option value="">No Role</option>
                          {roles.map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.role_name}
                            </option>
                          ))}
                        </select>
                      )}
                      <button
                        onClick={() => openPasswordModal(user)}
                        className="text-purple-600 hover:text-purple-900 p-2 rounded hover:bg-purple-50 transition-colors"
                        title="Update Password"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleBlockUnblock(user)}
                        className={`p-2 rounded transition-colors ${
                          user.is_blocked
                            ? "text-blue-600 hover:text-blue-900 hover:bg-blue-50"
                            : "text-orange-600 hover:text-orange-900 hover:bg-orange-50"
                        }`}
                        title={user.is_blocked ? "Unblock User" : "Block User"}
                      >
                        {user.is_blocked ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Password Update Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="px-6 py-4 border-b">
                <h3 className="text-xl font-bold">Update Password</h3>
              </div>
              <form onSubmit={handlePasswordUpdate} className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">New Password *</label>
                  <input
                    type="password"
                    required
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, newPassword: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Enter new password (min 8 characters)"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Confirm Password *</label>
                  <input
                    type="password"
                    required
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Confirm new password"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? "Updating..." : "Update Password"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordData({
                        userId: null,
                        newPassword: "",
                        confirmPassword: "",
                      });
                    }}
                    className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        </div>
      
  );
};

export default AdminDashboard;

